// SmartServicesList.jsx
import { useState, useMemo, useRef } from "react";
import ServiceCard from "./ServiceCard";
import { FiX } from "react-icons/fi";
import { motion } from "framer-motion";

const SmartServicesList = ({
  liste = [],
  // Seuils configurables via props
  thresholds = {
    veryLarge: 12, // 12+ services → afficher 12
    large: 8, // [8, 12[ → afficher 8
    medium: 4, // [4, 8[ → afficher 4
    small: 4, // <4 → tout afficher
  },
  // Config pour le nombre de colonnes responsive
  responsiveCols = {
    lg: 4, // lg et plus : 4 par ligne
    md: 3, // md : 3 par ligne
    sm: 1, // sm et moins : 1 par ligne
  },
  // Options pour la recherche
  search = true,
  searchPlaceholder = "Rechercher un service...",
  // Messages personnalisables
  emptyMessage = "Aucun service à afficher pour le moment.",
  loading = false,
  loadingMessage = "Chargement des services...",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef(null);

  // Filtrer les services actifs
  const activeServices = useMemo(() => {
    return Array.isArray(liste)
      ? liste.filter((service) => service.is_active === true)
      : [];
  }, [liste]);

  // Appliquer la recherche
  const filteredServices = useMemo(() => {
    const term = searchTerm?.trim().toLowerCase();
    if (!term) return activeServices;

    return activeServices.filter((service) => {
      const fields = [
        service?.nom,
        service?.description,
        service?.category?.name,
        service?.category?.description,
      ]
        .filter(Boolean)
        .map((f) => f.toLowerCase());

      return fields.some((field) => field.includes(term));
    });
  }, [activeServices, searchTerm]);

  // Déterminer le nombre d'éléments à afficher selon les seuils
  const itemsToDisplay = useMemo(() => {
    const count = filteredServices.length;

    if (count === 0) return { items: [], displayCount: 0 };
    if (count < 4) {
      // Moins de 4 : tout afficher
      return {
        items: filteredServices.slice(0, thresholds.small),
        displayCount: count,
      };
    }
    if (count < 8) {
      // [4, 8[ : afficher 4
      return {
        items: filteredServices.slice(0, thresholds.medium),
        displayCount: thresholds.medium,
      };
    }
    if (count < 12) {
      // [8, 12[ : afficher 8
      return {
        items: filteredServices.slice(0, thresholds.large),
        displayCount: thresholds.large,
      };
    }
    // 12 et plus : afficher 12
    return {
      items: filteredServices.slice(0, thresholds.veryLarge),
      displayCount: thresholds.veryLarge,
    };
  }, [filteredServices, thresholds]);

  // Déterminer le nombre de colonnes selon le nombre d'éléments à afficher
  const getGridColsClass = () => {
    const { displayCount } = itemsToDisplay;

    if (displayCount === 0) return "";

    // Pour moins de 4 services, on centre
    if (displayCount < 4) {
      return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 justify-center";
    }

    // Sinon, utiliser la config responsive
    return `grid-cols-${responsiveCols.sm} md:grid-cols-${responsiveCols.md} lg:grid-cols-${responsiveCols.lg}`;
  };

  // Déterminer la justification
  const getJustifyClass = () => {
    const { displayCount } = itemsToDisplay;
    return displayCount < 4 ? "justify-center" : "";
  };

  // Gérer l'affichage du message vide
  const renderEmptyState = () => (
    <div className="col-span-full text-center py-12">
      <p className="font-nunito text-[16px] text-gray-500">
        {searchTerm
          ? "Aucun service ne correspond à votre recherche."
          : emptyMessage}
      </p>
      {searchTerm && (
        <button
          onClick={() => setSearchTerm("")}
          className="mt-4 font-nunito text-[14px] text-[#FF4081] hover:underline"
        >
          Réinitialiser la recherche
        </button>
      )}
    </div>
  );

  // Gérer l'affichage du chargement
  const renderLoadingState = () => (
    <div className="col-span-full text-center py-12">
      <p className="font-nunito text-[16px] text-gray-500">{loadingMessage}</p>
      <div className="mt-4 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF4081]"></div>
      </div>
    </div>
  );

  return (
    <div className="w-full">
      {/* Barre de recherche */}
      {search && (
        <div className="mb-8">
          <div className="relative max-w-md mx-auto lg:mx-0 lg:ml-auto">
            <input
              ref={searchInputRef}
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-3 font-nunito text-[14px] text-text border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4081] focus:border-transparent"
            />
            {searchTerm && (
              <button
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
                aria-label="Effacer la recherche"
                onClick={() => {
                  setSearchTerm("");
                  searchInputRef.current?.focus();
                }}
              >
                <FiX size={18} className="text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Affichage principal */}
      <div className="w-full">
        {loading ? (
          renderLoadingState()
        ) : (
          <>
            {/* Liste des services */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className={`grid gap-6 ${getGridColsClass()} ${getJustifyClass()}`}
            >
              {itemsToDisplay.displayCount === 0
                ? renderEmptyState()
                : itemsToDisplay.items.map((service, index) => (
                    <ServiceCard service={service} key={service.id || index} />
                  ))}
            </motion.div>

            {/* Indicateur du nombre de services affichés */}
            {filteredServices.length > itemsToDisplay.displayCount && (
              <div className="mt-8 text-center">
                <p className="font-nunito text-[14px] text-gray-600">
                  Affichage de {itemsToDisplay.displayCount} services sur{" "}
                  {filteredServices.length} disponibles
                </p>
                {searchTerm &&
                  filteredServices.length > thresholds.veryLarge && (
                    <p className="font-nunito text-[13px] text-gray-500 mt-1">
                      Affinez votre recherche pour voir plus de résultats
                    </p>
                  )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SmartServicesList;
